import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Users, ImageIcon, MessagesSquare, Home, ArrowUpRight, ArrowRight, Calendar, Newspaper, Handshake, AlertTriangle, Settings, Package, Cloud, UsersRound, MessageSquare, Ticket } from "lucide-react";
import AnimatedNumber from "@/components/AnimatedNumber";
import Reveal from "@/components/Reveal";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);

  const pendingMembers = stats?.pending_members ?? 0;
  const sponsorRenewals = stats?.sponsor_renewals_due ?? 0;
  const sponsorEnquiries = stats?.new_sponsor_enquiries ?? 0;
  const sponsorBadge = sponsorRenewals + sponsorEnquiries;
  const contactEnquiries = stats?.new_contact_enquiries ?? 0;

  const approvedMembers = stats?.approved_members ?? 0;

  const pendingPhotos = stats?.pending_gallery ?? 0;

  const chatRooms = stats?.chat_rooms ?? 0;

  return (
    <AppShell variant="admin" title="Overview">

      <div className="mt-2">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Quick actions</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">Manage the club</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-3">
          {[
            { to: "/a/system", icon: Settings, label: "System Management", desc: "Platform settings & tools" },
            { to: "/a/landing", icon: Home, label: "Landing Page", desc: "Hero, settings & content" },
            { to: "/a/events", icon: Calendar, label: "Events", desc: "Schedule club events" },
            { to: "/a/news", icon: Newspaper, label: "News", desc: "Publish an update" },
            { to: "/a/members", icon: Users, label: "Member Management", desc: "Approve & manage", badge: pendingMembers },
            { to: "/a/equipment-loans", icon: Package, label: "Equipment Loans", desc: "Track gear & returns" },
            { to: "/a/teams", icon: UsersRound, label: "Team Selection", desc: "Pick & publish teams" },
            { to: "/a/playhq", icon: Cloud, label: "PlayHQ", desc: "Sync fixtures & results" },
            { to: "/a/gallery", icon: ImageIcon, label: "Gallery", desc: "Approve photos", badge: pendingPhotos },
            { to: "/a/chats", icon: MessagesSquare, label: "Chats", desc: "Create rooms", badge: chatRooms },
            { to: "/a/sponsors", icon: Handshake, label: "Sponsors", desc: "Manage partners", badge: sponsorBadge },
            { to: "/a/raffles", icon: Ticket, label: "Raffles", desc: "Manage raffle games" },
            { to: "/a/contact", icon: MessageSquare, label: "Contact Enquiries", desc: "View messages", badge: contactEnquiries },
          ].map((q, i) => (
            <Reveal key={q.to} delay={i * 60}>
              <Link to={q.to} data-testid={`admin-quick-${q.label}`} className="group">
                <Card className="p-2.5 h-full rounded-xl hover:border-foreground/40 transition-all hover-lift bg-[#faf8f5]">
                  <div className="flex items-start justify-between">
                    <div className="relative h-7 w-7 rounded-md bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center group-hover:bg-[hsl(var(--accent))] group-hover:text-white transition-colors">
                      <q.icon className="h-3.5 w-3.5" />
                      {q.badge > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold px-0.5" data-testid={`badge-${q.label}`}>
                          {q.badge}
                        </span>
                      )}
                    </div>
                    <ArrowUpRight className="h-3 w-3 text-muted-foreground group-hover:text-[hsl(var(--accent))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <div className="mt-2 font-semibold tracking-tight text-xs">{q.label}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{q.desc}</div>
                </Card>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
